import React from "react";
import { observer } from "mobx-react";
import { Badge, Box, Button, Flex, Loader } from "@mantine/core";
import { format, formatDistanceToNowStrict, parseISO } from "date-fns";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import Text from "components/Text/Text";
import { ReactComponent as TagIcon } from 'assets/tag.svg';
import { ReactComponent as AddTagIcon } from 'assets/tag-add.svg';
import { ReactComponent as CloseIcon } from "assets/close.svg";
import styles from './Summary.module.scss';
import { Tab } from "Modules/PractitionerBaseballCard";
import useExternalApiService from '../../../../helpers/ExternalApiService';
import { formatMoneyWithZero } from "helpers";
import { store } from "stores/Store";
import TagsMenu from "./TagsMenu";
import usePossibleDuplicates from "Modules/PractitionerBaseballCard/components/PossibleDuplicates/usePossibleDuplicates";
import { chipForState } from "Modules/PractitionerBaseballCard/components/PossibleDuplicates/helpers";
import duplicatesStyles from "Modules/PractitionerBaseballCard/components/PossibleDuplicates/PossibleDuplicates.module.scss";

interface ITag {
  id: number;
  name: string;
  selected: boolean;
}

interface ISummary {
  isLoadingFollowUpDates: boolean;
  isCreateTagMenuOpen: boolean;
  isEditTagsMenuOpen: boolean;
  navigate: (path: Tab) => void;
  onOpenFollowUpMenu: () => void;
  onClickEditFollowUpDate: () => void;
  onCompleteFollowUp: () => Promise<void>;
  onOpenCreateTagMenu: () => void;
  onOpenEditTagsMenu: () => void;
}

// EIS-1124 part A round 3, finding 1: this component reads MobX gating state
// (store.user.earlyRelease, and -- via usePossibleDuplicates -- the shared
// duplicates cache) directly in its body, but the parent that renders it
// does not observe store.user. Without `observer` here, a flag flip after
// mount (e.g. earlyRelease turning on mid-session) is invisible to this
// component and the chip/fetch would never activate. See Summary.test.tsx's
// "must react to store.user changing after mount" test.
const SummaryComponent = ({
  isLoadingFollowUpDates,
  isCreateTagMenuOpen,
  isEditTagsMenuOpen,
  navigate,
  onOpenFollowUpMenu,
  onClickEditFollowUpDate,
  onCompleteFollowUp,
  onOpenCreateTagMenu,
  onOpenEditTagsMenu,
}: ISummary) => {
  const apiUrl = process.env.REACT_APP_EAI_URL;
  const {postData, getData} = useExternalApiService(apiUrl);

  const [keystats, setKeystats] = React.useState<any>(null);
  const [isTagsMenuOpen, setIsTagsMenuOpen] = React.useState<boolean>(false);
  const [isSubmittingCompleteFollowUp, setIsSubmittingCompleteFollowUp] = React.useState<boolean>(false);

  // 2) Pull out the practitioner properties
  const practitionerName = store.currentPractitioner?.primary_name;
  const dataSource = store.currentPractitioner?.data_source;
  const status = store.currentPractitioner?.status;
  const tags = store.currentPractitioner?.tags?.mine ? store.currentPractitioner.tags : { mine: [], others: [] };
  const nextFollowUpDate = store.currentPractitionerNextFollowUpDate;
  const lastCheckInDate = store.currentPractitionerLastCheckinDate && store.currentPractitionerLastCheckinDate.includes('T') ? `${store.currentPractitionerLastCheckinDate}Z` : store.currentPractitioner?.last_checkin_date;

  // EIS-1124: possible-duplicates chip. Shares usePossibleDuplicates' module-
  // level cache with the Details tab's PossibleDuplicates panel, so the two
  // surfaces agree without a second /api/matching/for_practitioner call.
  // Same gating as the panel: nothing renders, and no fetch fires, unless
  // the rep is early-release and a practitioner is loaded.
  const duplicatesPractitionerId: number | undefined = store.currentPractitioner?.id;
  const duplicatesEnabled = Boolean(store.user?.earlyRelease) && duplicatesPractitionerId != null;
  const { data: duplicatesData } = usePossibleDuplicates(duplicatesPractitionerId, duplicatesEnabled);
  const duplicatesChip = duplicatesData ? chipForState(duplicatesData) : null;
  const duplicatesChipClass = duplicatesChip
    ? `${duplicatesStyles.stateChip} ${
        duplicatesChip.kind === 'verified'
          ? duplicatesStyles.stateChipVerified
          : duplicatesChip.kind === 'pending'
          ? duplicatesStyles.stateChipPending
          : duplicatesStyles.stateChipSuggestions
      }`
    : '';

  const mySelectedTags = tags.mine.filter((tag: { selected: boolean; }) => tag.selected);
  const otherSelectedTags = tags.others.filter((tag: { selected: boolean; }) => tag.selected);

  const shouldShowWarning =
    dataSource === "PRACTITIONER_ACCOUNT_VIEW_NM" ||
    status === "LEAD-INCOMPLETE" ||
    status === "LEAD-READY2SUBMIT";

  const closeTagsMenu = () => {
    setIsTagsMenuOpen(false);
  }

  const updatePractitionerCache = React.useCallback((updatedTags: { mine: ITag[], others: ITag[] }) => {
    if (!store.impersonatedUser) return;
    const currentUser = store.impersonatedUser;
    const userCache = store.practitionersCache[currentUser];
    if (userCache) {
      Object.keys(userCache).forEach(pageKey => {
        const page = parseInt(pageKey);
        const cachedPractitioners = store.getPractitionersCache(currentUser, page);
        if (cachedPractitioners) {
          const updatedCache = cachedPractitioners.map((practitioner: any) =>
            practitioner.id === store.currentPractitioner?.id
              ? {...practitioner, tags: updatedTags}
              : practitioner
          );
          store.setPractitionersCache(currentUser, page, updatedCache);
        }
      });
    }
  }, []);

  const refetchTags = React.useCallback(async () => {
    const response = await getData(`/api/practitioner/${store.currentPractitioner?.id}/tags`);
    store.setCurrentPractitioner({...store.currentPractitioner, tags: response.tags});
  }, [getData]);

  const handleSelectTag = async (tagId: number) => {
    const optimisticTags = tags.mine.map((tag: ITag) => 
      tag.id === tagId ? {...tag, selected: true} : tag
    );
    store.setCurrentPractitioner({...store.currentPractitioner, tags: {...tags, mine: optimisticTags}});
    updatePractitionerCache({...tags, mine: optimisticTags});
    
    try {
      await postData(`/api/practitioner/${store.currentPractitioner?.id}/tags/assign`, {tag_ids: [tagId]});
      refetchTags();
    } catch (error) {
      const revertedTags = tags.mine.map((tag: ITag) => 
        tag.id === tagId ? {...tag, selected: false} : tag
      );
      store.setCurrentPractitioner({...store.currentPractitioner, tags: {...tags, mine: revertedTags}});
      updatePractitionerCache({...tags, mine: revertedTags});
      console.error('Failed to assign tag:', error);
    }
  };

  const handleUnselectTag = async (tagId: number) => {
    const optimisticTags = tags.mine.map((tag: ITag) => 
      tag.id === tagId ? {...tag, selected: false} : tag
    );
    store.setCurrentPractitioner({...store.currentPractitioner, tags: {...tags, mine: optimisticTags}});
    updatePractitionerCache({...tags, mine: optimisticTags});
    
    try {
      await postData(`/api/practitioner/${store.currentPractitioner?.id}/tags/unassign`, {tag_ids: [tagId]});
      refetchTags();
    } catch (error) {
      const revertedTags = tags.mine.map((tag: ITag) => 
        tag.id === tagId ? {...tag, selected: true} : tag
      );
      store.setCurrentPractitioner({...store.currentPractitioner, tags: {...tags, mine: revertedTags}});
      updatePractitionerCache({...tags, mine: revertedTags});
      console.error('Failed to unassign tag:', error);
    }
  }

  const handleUnassignTag = (tagId: number) => async () => {
    const optimisticTags = tags.mine.map((tag: ITag) => 
      tag.id === tagId ? {...tag, selected: false} : tag
    );
    store.setCurrentPractitioner({...store.currentPractitioner, tags: {...tags, mine: optimisticTags}});
    updatePractitionerCache({...tags, mine: optimisticTags});
    
    try {
      await postData(`/api/practitioner/${store.currentPractitioner?.id}/tags/unassign`, {tag_ids: [tagId]});
      refetchTags();
    } catch (error) {
      const revertedTags = tags.mine.map((tag: ITag) => 
        tag.id === tagId ? {...tag, selected: true} : tag
      );
      store.setCurrentPractitioner({...store.currentPractitioner, tags: {...tags, mine: revertedTags}});
      updatePractitionerCache({...tags, mine: revertedTags});
      console.error('Failed to unassign tag:', error);
    }
  };

  const handleSubmitCompleteFollowUp = async () => {
    try {
      setIsSubmittingCompleteFollowUp(true);
      await onCompleteFollowUp();
    } finally {
      setIsSubmittingCompleteFollowUp(false);
    }
  }

  React.useEffect(() => {
    if (!practitionerName) return;

    const getKeystats = async () => {
      // Use NetSuite ID for accounts with valid netsuite_id,
      // but continue using name for PRACTITIONER_DISTRIBUTOR_ACCOUNT_VIEW
      const useNetsuiteId = store.currentPractitioner?.netsuite_id &&
                           store.currentPractitioner?.data_source !== "PRACTITIONER_DISTRIBUTOR_ACCOUNT_VIEW";

      const endpoint = useNetsuiteId
        ? '/api/e/call_winback_keystats_by_netsuite_id'
        : '/api/e/call_winback_keystats_by_practitioner_name';

      // EIS-1161: distributor cards were priced by matching primary_name
      // against t_historical.PRACTITIONER, which EIS-1114 rewrote -- so they
      // showed $0. Send dist_practitioner_id (already on the practitioner
      // payload, we just never read it) so the backend can match on the id
      // and fall back to the name only if it misses.
      const params = useNetsuiteId
        ? {
            netsuite_id: store.currentPractitioner?.netsuite_id,
            practitioner_name: store.currentPractitioner?.primary_name ?? '' // fallback for detection logic
          }
        : {
            practitioner_name: store.currentPractitioner?.primary_name ?? '',
            name: store.currentPractitioner?.name ?? '',
            practitioner_id: store.currentPractitioner?.dist_practitioner_id ?? null,
            channel: 'DISTRIBUTOR'
          };

      const response = await postData(endpoint, params);
      console.log(response.payload);
      setKeystats(response.payload);
    }
    getKeystats();
    refetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practitionerName]);

  // ── 2) Check warning conditions (data_source or status) ──────────────────────
  //
  // The three cases where we want to show a “warning” screen instead of Key Stats:
  //  • data_source === "PRACTITIONER_ACCOUNT_VIEW_NM"
  //  • status === "LEAD-INCOMPLETE"
  //  • status === "LEAD-READY2SUBMIT"
  //

  return(
    <>
      {duplicatesChip && (
        <div className={styles.sectionWrapper} style={{ padding: '10px 10px 0' }}>
          <button
            type="button"
            className={duplicatesChipClass}
            style={{ border: 'none' }}
            onClick={() => navigate(Tab.DETAILS)}
          >
            {duplicatesChip.label}
          </button>
        </div>
      )}
     {/*
      <div className={styles.sectionWrapper}>
        <p className={styles.headerText}>NEXT STEPS</p>
      </div>
        <div className={styles.horizontalScrollItemWrapper}>
          <Button type="outline">FOLLOW UP</Button>
        </div>
        <div className={styles.horizontalScrollItemWrapper}>
          <Button type="outline">xxxxxxx</Button>
        </div>
        <div className={styles.horizontalScrollItemWrapper}>
          <Button type="outline">xxxxxxx</Button>
        </div>
        
      </div>
      */}
      {/* <div className={styles.sectionWrapper} onClick={() => navigate(Tab.OPPORTUNITIES)}>
        <p className={styles.headerText}>OPPORTUNITIES<ExpandIcon /></p>
      </div>
      <div className={styles.rowWrapper}>
        <div className={styles.columnWrapper}>
          <p className={styles.HugeText}>$56K</p>
          <Text>Opportunity by March 20</Text>
        </div>
        <div className={styles.columnWrapper}>
          <Text bold>1. GI Revive: 56 units</Text>
          <Text bold>2. CoQnol: 30 units</Text>
          <Text bold>3. Spotlight: 30 units</Text>
        </div>
      </div> */}
      <div className={styles.sectionWrapper}>
        <p className={styles.headerText}>NEXT STEPS</p>
        <Box p={10}>
          <Flex
            gap={10}
            mb={15}
            justify="space-between"
            align="center"
            rowGap={10}
          >
            <div>
              <strong>Follow-Up</strong>
              {isLoadingFollowUpDates ? (
                <Loader size="sm" ml={10} />
              ) : nextFollowUpDate ? (
                <Flex display="inline" align="center">
                  <span> on </span>
                  <Button
                    variant="white"
                    onClick={onClickEditFollowUpDate}
                    classNames={{ root: styles.editFollowUpDateButton }}
                  >
                    {format(parseISO(nextFollowUpDate), 'MMM. dd, yyyy')}
                  </Button>
                </Flex>
              ) : (
                <span className={styles.followUpNotSet}> not set</span>
              )}
            </div>
            {nextFollowUpDate ? (
              <Button
                type="button"
                onClick={handleSubmitCompleteFollowUp}
                color="#58E487"
                classNames={{ root: styles.followUpMenuButton }}
                disabled={isSubmittingCompleteFollowUp}
                loading={isSubmittingCompleteFollowUp}
              >
                COMPLETE FOLLOW-UP
              </Button>
            ) : (
              <Button
                type="button"
                onClick={onOpenFollowUpMenu}
                color="#E64B37"
                classNames={{ root: styles.followUpMenuButton }}
              >
                SET FOLLOW-UP
              </Button>
            )}
          </Flex>
          {isLoadingFollowUpDates ? (
            <Flex align="center" gap={10} style={{ fontSize: '14px' }}>
              <strong>Last Check-In</strong>
              <Loader size="sm" />
            </Flex>
          ) : lastCheckInDate ? (
            <span className={styles.lastCheckinText}>
              <strong>Last Check-In</strong> on {format(parseISO(lastCheckInDate), 'MMM. dd yyyy')} ({formatDistanceToNowStrict(parseISO(lastCheckInDate), {addSuffix: true, unit: 'day', roundingMethod: 'floor'})})
            </span>
          ) : (
            <span className={styles.noCheckinText}>
              No recorded last check-in
            </span>
          )}
        </Box>
      </div>
      <div className={styles.sectionWrapper}>
        <Flex
          className={styles.headerText}
          m={0}
          gap={10}
        >
          <Flex gap={5} align="center"><span>TAGS</span> <TagIcon /></Flex>
          {(mySelectedTags.length > 0 || otherSelectedTags.length > 0) && (
            <>
              <Badge classNames={{ root: styles.tagBadgeMine }} style={{ fontWeight: 'normal', textTransform: 'none' }}>
                Your Tags
              </Badge>
              <Badge classNames={{ root: styles.tagBadgeOther }} style={{ fontWeight: 'normal', textTransform: 'none' }}>
                Other Users' Tags
              </Badge>
            </>
          )}
        </Flex>
        <Flex
          gap={10}
          p={10}
          justify="center"
          align="center"
        >
          {mySelectedTags.length === 0 && otherSelectedTags.length === 0 ? (
            <p className={styles.tagsEmpty}>
              Tags allow you to create <strong>custom labels</strong> to be used in account search
            </p>
          ) : (
            <Flex flex={1} gap={10} wrap="wrap">
              {mySelectedTags.map((tag: { id: number; name: string; }) => (
                <Badge
                  key={tag.id}
                  classNames={{ root: styles.tagBadgeMine }}
                  rightSection={(
                    <button
                      type="button"
                      className={styles.unassignTagButton}
                      onClick={handleUnassignTag(tag.id)}
                    >
                      <CloseIcon width={16} />
                    </button>
                  )}
                >
                  {tag.name}
                </Badge>
              ))}
              {otherSelectedTags.map((tag: { id: number; name: string; }) => (
                <Badge
                  key={tag.id}
                  classNames={{ root: styles.tagBadgeOther }}
                >
                  {tag.name}
                </Badge>
              ))}
            </Flex>
          )}
          <TagsMenu
            isOpen={isTagsMenuOpen}
            tags={tags.mine}
            triggerButton={(
              <button
                type="button"
                className={styles.addTagButton}
                onClick={() => setIsTagsMenuOpen(true)}
              >
                <AddTagIcon />
              </button>
            )}
            isCreateTagMenuOpen={isCreateTagMenuOpen}
            isEditTagsMenuOpen={isEditTagsMenuOpen}
            onOpenCreateTagMenu={onOpenCreateTagMenu}
            onOpenEditTagsMenu={onOpenEditTagsMenu}
            onSubmitTagSelect={handleSelectTag}
            onSubmitTagUnselect={handleUnselectTag}
            onClose={closeTagsMenu}
          />
        </Flex>
      </div>
      {shouldShowWarning ? (
        <div className={styles["summary-warning-container"]}>
          <p className={styles["summary-warning-text"]}>
            To view sales summary, this practitioner's baseball card needs to be matched in Netsuite to pull sales data.
          </p>
        </div>
      ) : (
        <div className={styles.sectionWrapper}>
          <p className={styles.headerText}>KEY STATS{/*<ExpandIcon />*/}</p>
          <div style={{ display: 'flex', gap: '20px', padding: '10px' }}>
            {/* YTD Section */}
            <div style={{ flex: 1, textAlign: 'center' }}>
              <p style={{ fontSize: '28px', fontWeight: '600', margin: 0 }}>
                {formatMoneyWithZero(keystats?.ytd_sales)}
              </p>
              <p style={{ fontSize: '14px', fontWeight: '600', color: '#1a365d', margin: '4px 0 12px 0' }}>
                Sales (YTD)
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '24px' }}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: '14px', fontWeight: '500', margin: 0 }}>
                    {formatMoneyWithZero(keystats?.p_ytd_sales)}
                  </p>
                  <p style={{ fontSize: '10px', color: '#1a365d', fontWeight: '600', margin: '2px 0 0 0', textTransform: 'uppercase' }}>
                    Sales (PYTD)
                  </p>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: '14px', fontWeight: '500', margin: 0 }}>
                    {keystats?.p_ytd_sales ? `${(((keystats?.ytd_sales || 0) - keystats.p_ytd_sales) / keystats.p_ytd_sales * 100).toFixed(1)}%` : 'N/A'}
                  </p>
                  <p style={{ fontSize: '10px', color: '#1a365d', fontWeight: '600', margin: '2px 0 0 0', textTransform: 'uppercase' }}>
                    vs PY
                  </p>
                </div>
              </div>
            </div>
            {/* MTD Section */}
            <div style={{ flex: 1, textAlign: 'center' }}>
              <p style={{ fontSize: '28px', fontWeight: '600', margin: 0 }}>
                {formatMoneyWithZero(keystats?.mtd_sales)}
              </p>
              <p style={{ fontSize: '14px', fontWeight: '600', color: '#1a365d', margin: '4px 0 12px 0' }}>
                Sales (MTD)
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '24px' }}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: '14px', fontWeight: '500', margin: 0 }}>
                    {formatMoneyWithZero(keystats?.p_mtd_sales)}
                  </p>
                  <p style={{ fontSize: '10px', color: '#1a365d', fontWeight: '600', margin: '2px 0 0 0', textTransform: 'uppercase' }}>
                    Sales (PMTD)
                  </p>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: '14px', fontWeight: '500', margin: 0 }}>
                    {keystats?.p_mtd_sales ? `${(((keystats?.mtd_sales || 0) - keystats.p_mtd_sales) / keystats.p_mtd_sales * 100).toFixed(1)}%` : 'N/A'}
                  </p>
                  <p style={{ fontSize: '10px', color: '#1a365d', fontWeight: '600', margin: '2px 0 0 0', textTransform: 'uppercase' }}>
                    vs PY
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Rolling 12 Months Section */}
          <div style={{ textAlign: 'center', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #eee' }}>
            <p style={{ fontSize: '28px', fontWeight: '600', margin: 0 }}>
              {formatMoneyWithZero(keystats?.rolling_12m_sales)}
            </p>
            <p style={{ fontSize: '14px', fontWeight: '600', color: '#1a365d', margin: '4px 0 0 0' }}>
              Total Sales (Rolling 12 Months)
            </p>
          </div>
        </div>
      )}
      {/* <div className={styles.twoColumnWrapper}>
        <p className={styles.ordersText}>15</p>
        <p className={styles.HugeText}>XXX</p>
        <Text textAlign="center" bold>Open Orders</Text>
        <Text textAlign="center" bold>YYY Metric</Text>
      </div> */}
    </>
  )
}

const Summary = observer(SummaryComponent);

export default Summary;